import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where, orderBy, Timestamp } from "firebase/firestore";
import { db, functions } from "../../lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useAuthContext } from "../../contexts/AuthContext";
import { CheckCircle, XCircle, Clock, ArrowLeft, Home, User, DollarSign, Calendar, FileText, ExternalLink, Search, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import Card from "../../components/Card";

interface UnifiedPayment {
  id: string;
  type: 'course' | 'salepage' | 'trial' | 'addon';
  userId: string;
  email: string;
  name: string;
  tier?: string;
  plan?: string;
  amount: number;
  billingPeriod?: string;
  slipUrl?: string;
  slipImageUrl?: string;
  note?: string;
  createdAt: any;
  status: "pending" | "approved" | "rejected" | "verified" | "expired";
  promptPayRef?: string;
  addons?: {
    hasPathUrl?: boolean;
    hasSubdomain?: boolean;
    username?: string;
  };
  addonName?: string;
  storageGB?: number;
}

export default function UnifiedPaymentApproval() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<UnifiedPayment[]>([]);
  const [processing, setProcessing] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<UnifiedPayment | null>(null);

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }

    loadAllPayments();
  }, [user, navigate, filter]);

  const loadAllPayments = async () => {
    setLoading(true);
    try {
      const allPayments: UnifiedPayment[] = [];

      // Load Course Subscription Payments
      let courseQuery;
      if (filter === 'all') {
        courseQuery = collection(db, "subscription_payments");
      } else {
        courseQuery = query(
          collection(db, "subscription_payments"),
          where("status", "==", filter === 'approved' ? 'approved' : 'pending')
        );
      }
      
      const courseSnap = await getDocs(courseQuery);
      courseSnap.docs.forEach(doc => {
        const data = doc.data();
        allPayments.push({
          id: doc.id,
          type: 'course',
          userId: data.instructorId || data.userId,
          email: data.instructorEmail || data.email,
          name: data.instructorName || data.name || 'ไม่ระบุชื่อ',
          tier: data.tier,
          amount: data.amount,
          slipUrl: data.slipUrl,
          note: data.note,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status
        });
      });

      // Load Salepage Payments
      let salePageQuery;
      if (filter === 'all') {
        salePageQuery = collection(db, "salePagePayments");
      } else {
        salePageQuery = query(
          collection(db, "salePagePayments"),
          where("status", "==", filter === 'approved' ? 'verified' : 'pending')
        );
      }
      
      const salePageSnap = await getDocs(salePageQuery);
      salePageSnap.docs.forEach(doc => {
        const data = doc.data();
        allPayments.push({
          id: doc.id,
          type: 'salepage',
          userId: data.userId,
          email: data.email,
          name: data.email.split('@')[0],
          plan: data.plan,
          amount: data.amount,
          billingPeriod: data.billingPeriod,
          slipImageUrl: data.slipImageUrl,
          promptPayRef: data.promptPayRef,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status === 'verified' ? 'approved' : data.status,
          addons: data.addons
        });
      });

      // Load Salepage Payment Notifications
      let notificationsQuery;
      if (filter === 'all') {
        notificationsQuery = collection(db, "salePagePaymentNotifications");
      } else {
        notificationsQuery = query(
          collection(db, "salePagePaymentNotifications"),
          where("status", "==", filter === 'approved' ? 'verified' : 'pending')
        );
      }
      
      const notificationsSnap = await getDocs(notificationsQuery);
      notificationsSnap.docs.forEach(doc => {
        const data = doc.data();
        allPayments.push({
          id: doc.id,
          type: 'salepage',
          userId: data.userId,
          email: data.userEmail || data.email,
          name: data.userEmail?.split('@')[0] || data.email?.split('@')[0] || 'ไม่ระบุชื่อ',
          plan: data.planName || data.plan,
          amount: data.finalAmount || data.amount,
          billingPeriod: data.isRenewal ? 'renewal' : 'new',
          slipImageUrl: data.slipImageUrl,
          promptPayRef: data.promptPayRef,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status === 'verified' ? 'approved' : data.status,
          note: data.discountCode ? `ใช้โค้ดส่วนลด: ${data.discountCode} (${data.discountPercentage}%)` : undefined
        });
      });

      // Load Trial Requests (from users collection with pending trial status)
      const trialQuery = query(
        collection(db, "users"),
        where("salePageSubscription.status", "==", "trial_pending")
      );
      const trialSnap = await getDocs(trialQuery);
      trialSnap.docs.forEach(doc => {
        const data = doc.data();
        const salePageSub = data.salePageSubscription;
        if (salePageSub && salePageSub.trialRequestedAt) {
          allPayments.push({
            id: doc.id,
            type: 'trial',
            userId: doc.id,
            email: data.email,
            name: data.displayName || data.email.split('@')[0],
            amount: 0,
            createdAt: salePageSub.trialRequestedAt?.toDate() || new Date(),
            status: 'pending',
            billingPeriod: 'trial'
          });
        }
      });

      // Load Add-on Payments
      let addonQuery;
      if (filter === 'all') {
        addonQuery = collection(db, "addonPayments");
      } else {
        addonQuery = query(
          collection(db, "addonPayments"),
          where("status", "==", filter === 'approved' ? 'approved' : 'pending')
        );
      }
      
      const addonSnap = await getDocs(addonQuery);
      addonSnap.docs.forEach(doc => {
        const data = doc.data();
        allPayments.push({
          id: doc.id,
          type: 'addon',
          userId: data.userId,
          email: data.email,
          name: data.email.split('@')[0],
          amount: data.amount,
          billingPeriod: data.billingPeriod,
          slipImageUrl: data.slipImageUrl,
          promptPayRef: data.promptPayRef,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status,
          addonName: data.addonName,
          storageGB: data.storageGB
        });
      });

      // Load Payment Notifications (Subscription Renewals & Storage Add-ons)
      let paymentNotifQuery;
      if (filter === 'all') {
        paymentNotifQuery = collection(db, "paymentNotifications");
      } else {
        paymentNotifQuery = query(
          collection(db, "paymentNotifications"),
          where("status", "==", filter === 'approved' ? 'approved' : 'pending')
        );
      }
      
      const paymentNotifSnap = await getDocs(paymentNotifQuery);
      paymentNotifSnap.docs.forEach(doc => {
        const data = doc.data();
        // Determine type based on payment data
        let paymentType: 'course' | 'addon' = 'addon';
        if (data.plan || data.planName || data.isQuickRenew) {
          paymentType = 'course'; // This is a subscription payment (upgrade or renewal)
        }
        
        allPayments.push({
          id: doc.id,
          type: paymentType,
          userId: data.userId,
          email: data.userEmail,
          name: data.userEmail?.split('@')[0] || 'ไม่ระบุชื่อ',
          tier: data.plan,
          plan: data.planName,
          amount: data.amount,
          billingPeriod: data.isQuickRenew ? 'renewal' : 'one-time',
          slipImageUrl: data.paymentProofUrl,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status,
          addonName: data.isQuickRenew ? `ต่ออายุ ${data.planName}` : (data.storageAddon ? `Storage +${data.storageAddon}GB` : 'Add-on'),
          storageGB: data.storageAddon,
          note: data.isQuickRenew && data.addons ? `พร้อม Add-ons ${data.addons.length} รายการ` : undefined
        });
      });

      // Sort by date
      allPayments.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setPayments(allPayments);
    } catch (error) {
      console.error("Error loading payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (payment: UnifiedPayment) => {
    if (!confirm(`ยืนยันการอนุมัติ${payment.type === 'trial' ? 'การทดลองใช้ฟรี' : 'การชำระเงิน'}ของ ${payment.name}?`)) return;

    setProcessing(payment.id);
    try {
      if (payment.type === 'course') {
        // Check which collection this payment is from
        const oldPaymentDocRef = doc(db, 'subscription_payments', payment.id);
        const newPaymentDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const oldPaymentDoc = await getDoc(oldPaymentDocRef);
        const newPaymentDoc = await getDoc(newPaymentDocRef);
        
        if (oldPaymentDoc.exists()) {
          // Old payment system
          await updateDoc(doc(db, "subscription_payments", payment.id), {
            status: "approved",
            approvedAt: new Date(),
            approvedBy: user?.email
          });

          // Update instructor's subscription - use userId from payment
          if (payment.userId) {
            await updateDoc(doc(db, "users", payment.userId), {
              subscriptionTier: payment.tier,
              subscriptionStatus: "active",
              subscriptionStartDate: new Date(),
              subscriptionEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            });
          }

          // Send email notification
          const sendEmail = httpsCallable(functions, 'sendPaymentApprovalEmail');
          await sendEmail({
            email: payment.email,
            name: payment.name,
            type: 'course',
            tier: payment.tier
          });
        } else if (newPaymentDoc.exists()) {
          // New payment notification system
          const paymentData = newPaymentDoc.data();
          
          // Update payment notification status
          await updateDoc(doc(db, "paymentNotifications", payment.id), {
            status: "approved",
            approvedAt: new Date(),
            approvedBy: user?.email
          });

          // Calculate subscription end date based on plan
          const plan = paymentData.plan || payment.tier;
          let daysToAdd = 30; // Default monthly
          if (plan?.includes('yearly')) {
            daysToAdd = 365;
          } else if (plan?.includes('semi_annual')) {
            daysToAdd = 180;
          }

          // Update user's subscription - use userId directly
          const userDocRef = doc(db, "users", payment.userId);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const currentEndDate = userDocSnap.data().subscriptionEndDate?.toDate();
            const now = new Date();
            
            // If current subscription is still active, extend from end date, otherwise start from now
            const startDate = currentEndDate && currentEndDate > now ? currentEndDate : now;
            const endDate = new Date(startDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

            await updateDoc(userDocRef, {
              subscriptionTier: plan,
              subscriptionStatus: "active",
              subscriptionEndDate: endDate,
              updatedAt: new Date()
            });

            // Try to update subscription record if it exists (using userId as doc ID)
            try {
              const subscriptionDocRef = doc(db, "subscriptions", payment.userId);
              const subscriptionDoc = await getDoc(subscriptionDocRef);
              
              if (subscriptionDoc.exists()) {
                await updateDoc(subscriptionDocRef, {
                  tier: plan,
                  endDate: endDate,
                  status: "active",
                  updatedAt: new Date()
                });
              }
            } catch (error) {
              console.log("No subscription document to update, user document is sufficient");
            }
          }
        }

      } else if (payment.type === 'salepage') {
        // Check which collection this payment is from
        const paymentDocRef = doc(db, 'salePagePayments', payment.id);
        const notificationDocRef = doc(db, 'salePagePaymentNotifications', payment.id);
        
        const paymentDoc = await getDoc(paymentDocRef);
        const notificationDoc = await getDoc(notificationDocRef);
        
        if (paymentDoc.exists()) {
          // Old payment system
          const verifyPayment = httpsCallable(functions, 'verifySalePagePayment');
          await verifyPayment({
            paymentId: payment.id,
            approved: true,
            adminNote: 'Approved by admin'
          });

          const sendEmail = httpsCallable(functions, 'sendSalePageApprovalEmail');
          await sendEmail({
            email: payment.email,
            userId: payment.userId,
            paymentId: payment.id
          });
        } else if (notificationDoc.exists()) {
          // New notification system
          await updateDoc(doc(db, 'salePagePaymentNotifications', payment.id), {
            status: 'verified',
            approvedAt: new Date(),
            approvedBy: user?.email
          });

          // Update user's salepage subscription
          const userData = notificationDoc.data();
          const plan = userData.plan || 'premium';
          const endDate = new Date();
          endDate.setMonth(endDate.getMonth() + 1);

          await updateDoc(doc(db, 'users', payment.userId), {
            'salePageSubscription.status': 'active',
            'salePageSubscription.plan': plan,
            'salePageSubscription.startDate': new Date(),
            'salePageSubscription.endDate': endDate,
            'salePageSubscription.lastPaymentDate': new Date()
          });
        }

      } else if (payment.type === 'trial') {
        // Approve trial
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 7);

        await updateDoc(doc(db, "users", payment.userId), {
          'salePageSubscription.status': 'trial',
          'salePageSubscription.plan': 'start',
          'salePageSubscription.startDate': new Date(),
          'salePageSubscription.endDate': trialEndDate,
          'salePageSubscription.approvedAt': new Date(),
          'salePageSubscription.approvedBy': user?.email
        });

        // Send email notification with verification link
        const sendEmail = httpsCallable(functions, 'sendTrialApprovalEmail');
        await sendEmail({
          email: payment.email,
          userId: payment.userId
        });

      } else if (payment.type === 'addon') {
        // Check which collection this payment is from
        const addonPaymentDocRef = doc(db, 'addonPayments', payment.id);
        const paymentNotifDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const addonPaymentDoc = await getDoc(addonPaymentDocRef);
        const paymentNotifDoc = await getDoc(paymentNotifDocRef);
        
        if (addonPaymentDoc.exists()) {
          // Old addon payment system
          await updateDoc(doc(db, "addonPayments", payment.id), {
            status: "approved",
            approvedAt: new Date(),
            approvedBy: user?.email
          });
        } else if (paymentNotifDoc.exists()) {
          // New payment notification system
          await updateDoc(doc(db, "paymentNotifications", payment.id), {
            status: "approved",
            approvedAt: new Date(),
            approvedBy: user?.email
          });
        }

        // Update user's subscription with addon storage - use userId as doc ID
        try {
          const subscriptionDocRef = doc(db, "subscriptions", payment.userId);
          const subscriptionDoc = await getDoc(subscriptionDocRef);
          
          if (subscriptionDoc.exists()) {
            const currentAddons = subscriptionDoc.data().addons || [];
            
            // Add new addon
            const newAddon = {
              id: payment.id,
              storageGB: payment.storageGB,
              addedAt: new Date(),
              billingPeriod: payment.billingPeriod,
              amount: payment.amount
            };
            
            await updateDoc(subscriptionDocRef, {
              addons: [...currentAddons, newAddon]
            });
            
            console.log("✅ Storage addon added to subscription:", payment.storageGB, "GB");
          } else {
            console.error("❌ Subscription document not found for userId:", payment.userId);
            alert("⚠️ ไม่พบข้อมูล subscription ของผู้ใช้ กรุณาตรวจสอบ");
          }
        } catch (error) {
          console.error("Error updating subscription with addon:", error);
          alert("⚠️ เกิดข้อผิดพลาดในการเพิ่ม addon: " + error);
        }
      }

      alert("✅ อนุมัติสำเร็จ");
      loadAllPayments();
    } catch (error) {
      console.error("Error approving payment:", error);
      alert("เกิดข้อผิดพลาด: " + error);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (payment: UnifiedPayment) => {
    const reason = prompt("เหตุผลในการปฏิเสธ (ถ้ามี):");
    if (reason === null) return;

    setProcessing(payment.id);
    try {
      if (payment.type === 'course') {
        // Check which collection this payment is from
        const oldPaymentDocRef = doc(db, 'subscription_payments', payment.id);
        const newPaymentDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const oldPaymentDoc = await getDoc(oldPaymentDocRef);
        const newPaymentDoc = await getDoc(newPaymentDocRef);
        
        if (oldPaymentDoc.exists()) {
          await updateDoc(doc(db, "subscription_payments", payment.id), {
            status: "rejected",
            rejectedAt: new Date(),
            rejectedBy: user?.email,
            rejectionReason: reason
          });
        } else if (newPaymentDoc.exists()) {
          await updateDoc(doc(db, "paymentNotifications", payment.id), {
            status: "rejected",
            rejectedAt: new Date(),
            rejectedBy: user?.email,
            rejectionReason: reason
          });
        }
      } else if (payment.type === 'salepage') {
        // Check which collection this payment is from
        const paymentDocRef = doc(db, 'salePagePayments', payment.id);
        const notificationDocRef = doc(db, 'salePagePaymentNotifications', payment.id);
        
        const paymentDoc = await getDoc(paymentDocRef);
        const notificationDoc = await getDoc(notificationDocRef);
        
        if (paymentDoc.exists()) {
          const verifyPayment = httpsCallable(functions, 'verifySalePagePayment');
          await verifyPayment({
            paymentId: payment.id,
            approved: false,
            adminNote: reason || 'Rejected by admin'
          });
        } else if (notificationDoc.exists()) {
          await updateDoc(doc(db, 'salePagePaymentNotifications', payment.id), {
            status: 'rejected',
            rejectedAt: new Date(),
            rejectedBy: user?.email,
            rejectionReason: reason
          });
        }
      } else if (payment.type === 'trial') {
        await updateDoc(doc(db, "users", payment.userId), {
          'salePageSubscription.status': 'rejected',
          'salePageSubscription.rejectedAt': new Date(),
          'salePageSubscription.rejectedBy': user?.email,
          'salePageSubscription.rejectionReason': reason
        });
      } else if (payment.type === 'addon') {
        // Check which collection this payment is from
        const addonPaymentDocRef = doc(db, 'addonPayments', payment.id);
        const paymentNotifDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const addonPaymentDoc = await getDoc(addonPaymentDocRef);
        const paymentNotifDoc = await getDoc(paymentNotifDocRef);
        
        if (addonPaymentDoc.exists()) {
          await updateDoc(doc(db, "addonPayments", payment.id), {
            status: "rejected",
            rejectedAt: new Date(),
            rejectedBy: user?.email,
            rejectionReason: reason
          });
        } else if (paymentNotifDoc.exists()) {
          await updateDoc(doc(db, "paymentNotifications", payment.id), {
            status: "rejected",
            rejectedAt: new Date(),
            rejectedBy: user?.email,
            rejectionReason: reason
          });
        }
      }

      alert("✅ ปฏิเสธแล้ว");
      loadAllPayments();
    } catch (error) {
      console.error("Error rejecting payment:", error);
      alert("เกิดข้อผิดพลาด: " + error);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (payment: UnifiedPayment) => {
    if (!confirm(`คุณแน่ใจหรือไม่ที่จะลบรายการนี้?\n${payment.name} - ${payment.email}`)) {
      return;
    }

    setProcessing(payment.id);
    try {
      if (payment.type === 'course') {
        // Check which collection this payment is from
        const oldPaymentDocRef = doc(db, 'subscription_payments', payment.id);
        const newPaymentDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const oldPaymentDoc = await getDoc(oldPaymentDocRef);
        const newPaymentDoc = await getDoc(newPaymentDocRef);
        
        if (oldPaymentDoc.exists()) {
          await deleteDoc(doc(db, "subscription_payments", payment.id));
        } else if (newPaymentDoc.exists()) {
          await deleteDoc(doc(db, "paymentNotifications", payment.id));
        }
      } else if (payment.type === 'salepage') {
        // Check which collection this payment is from
        const paymentDocRef = doc(db, 'salePagePayments', payment.id);
        const notificationDocRef = doc(db, 'salePagePaymentNotifications', payment.id);
        
        const paymentDoc = await getDoc(paymentDocRef);
        const notificationDoc = await getDoc(notificationDocRef);
        
        if (paymentDoc.exists()) {
          await deleteDoc(doc(db, "salePagePayments", payment.id));
        } else if (notificationDoc.exists()) {
          await deleteDoc(doc(db, "salePagePaymentNotifications", payment.id));
        }
      } else if (payment.type === 'addon') {
        // Check which collection this payment is from
        const addonPaymentDocRef = doc(db, 'addonPayments', payment.id);
        const paymentNotifDocRef = doc(db, 'paymentNotifications', payment.id);
        
        const addonPaymentDoc = await getDoc(addonPaymentDocRef);
        const paymentNotifDoc = await getDoc(paymentNotifDocRef);
        
        if (addonPaymentDoc.exists()) {
          await deleteDoc(doc(db, "addonPayments", payment.id));
        } else if (paymentNotifDoc.exists()) {
          await deleteDoc(doc(db, "paymentNotifications", payment.id));
        }
      }

      alert("✅ ลบรายการสำเร็จ");
      loadAllPayments();
    } catch (error) {
      console.error("Error deleting payment:", error);
      alert("เกิดข้อผิดพลาด: " + error);
    } finally {
      setProcessing(null);
    }
  };


  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.email.toLowerCase().includes(search) ||
      payment.name.toLowerCase().includes(search) ||
      payment.promptPayRef?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      course: "คอร์สเรียน",
      salepage: "Salepage",
      trial: "ทดลองใช้ฟรี",
      addon: "Add-on (พื้นที่เพิ่ม)"
    };
    return labels[type] || type;
  };

  const getTypeBadge = (type: string) => {
    if (type === 'course') return "bg-blue-100 text-blue-700";
    if (type === 'salepage') return "bg-blue-100 text-blue-700";
    if (type === 'trial') return "bg-green-100 text-green-700";
    if (type === 'addon') return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  const pendingCount = payments.filter(p => p.status === 'pending').length;
  const totalAmount = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark">อนุมัติการชำระเงิน</h1>
                <p className="text-textLight">ตรวจสอบและอนุมัติการชำระเงินทั้งหมด (คอร์สเรียน, Salepage, Trial, Add-ons)</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">รอการอนุมัติ</p>
                  <p className="text-3xl font-bold text-yellow-600">{pendingCount}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ยอดรวมที่รอ</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalAmount)}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้ใช้ที่รอ</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {new Set(payments.filter(p => p.status === 'pending').map(p => p.userId)).size}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="ค้นหาด้วยอีเมล, ชื่อ, หรือเลขอ้างอิง..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ทั้งหมด
                  </button>
                  <button
                    onClick={() => setFilter('pending')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    รอตรวจสอบ
                  </button>
                  <button
                    onClick={() => setFilter('approved')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    อนุมัติแล้ว
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Payments List */}
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-textDark mb-6">รายการทั้งหมด</h2>

            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-textLight text-lg">ไม่มีรายการ</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPayments.map((payment) => {
                  return (
                    <div 
                      key={`${payment.type}-${payment.id}`}
                      className={`border-2 rounded-xl overflow-hidden hover:shadow-lg transition-all ${
                        payment.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                            payment.status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'
                          }`}>
                            <User className="w-8 h-8 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div>
                                <h3 className="text-xl font-bold text-textDark">{payment.name}</h3>
                                <p className="text-sm text-textLight">{payment.email}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">
                                  {payment.amount === 0 ? 'ฟรี' : formatCurrency(payment.amount)}
                                </p>
                                <p className="text-xs text-textLight">
                                  {formatDate(payment.createdAt)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-3">
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTypeBadge(payment.type)}`}>
                                {getTypeLabel(payment.type)}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {payment.status === 'pending' ? 'รอการอนุมัติ' : 'อนุมัติแล้ว'}
                              </span>
                              {payment.tier && (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700">
                                  {payment.tier.toUpperCase()}
                                </span>
                              )}
                              {payment.billingPeriod && (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                                  {payment.billingPeriod === 'yearly' ? 'รายปี' : payment.billingPeriod === 'trial' ? 'ทดลอง 7 วัน' : 'รายเดือน'}
                                </span>
                              )}
                            </div>

                            {/* Add-on Details */}
                            {payment.type === 'addon' && (
                              <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
                                <p className="text-sm font-semibold text-blue-800 mb-2">📦 รายละเอียด Storage Add-on:</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-xs text-blue-600">แพ็กเกจ</p>
                                    <p className="font-bold text-blue-900">{payment.addonName}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-blue-600">พื้นที่เพิ่ม</p>
                                    <p className="font-bold text-blue-900">+{payment.storageGB} GB</p>
                                  </div>
                                  {payment.promptPayRef && (
                                    <div className="col-span-2">
                                      <p className="text-xs text-blue-600">เลขอ้างอิง PromptPay</p>
                                      <p className="font-mono font-bold text-blue-900">{payment.promptPayRef}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Slip Image */}
                            {(payment.slipUrl || payment.slipImageUrl) && (
                              <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                                <p className="text-sm font-semibold text-textDark mb-2">💳 สลิปการโอนเงิน:</p>
                                <img 
                                  src={payment.slipUrl || payment.slipImageUrl} 
                                  alt="Payment Slip" 
                                  className="max-w-xs rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 transition-all"
                                  onClick={() => setSelectedPayment(payment)}
                                />
                                <p className="text-xs text-gray-500 mt-2">คลิกเพื่อดูขนาดเต็ม</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                          {payment.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => handleApprove(payment)}
                                disabled={processing === payment.id}
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                              >
                                <CheckCircle className="w-6 h-6" />
                                {processing === payment.id ? "กำลังอนุมัติ..." : "✅ อนุมัติ"}
                              </button>
                              <button
                                onClick={() => handleReject(payment)}
                                disabled={processing === payment.id}
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                              >
                                <XCircle className="w-6 h-6" />
                                {processing === payment.id ? "กำลังปฏิเสธ..." : "❌ ปฏิเสธ"}
                              </button>
                              <button
                                onClick={() => handleDelete(payment)}
                                disabled={processing === payment.id}
                                className="px-6 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                              >
                                <Trash2 className="w-5 h-5" />
                                ลบ
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleDelete(payment)}
                              disabled={processing === payment.id}
                              className="w-full px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                            >
                              <Trash2 className="w-5 h-5" />
                              ลบรายการนี้
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Detail Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPayment(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">รายละเอียด</h2>
                <button onClick={() => setSelectedPayment(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {(selectedPayment.slipUrl || selectedPayment.slipImageUrl) && (
                <img 
                  src={selectedPayment.slipUrl || selectedPayment.slipImageUrl} 
                  alt="Payment Slip" 
                  className="w-full rounded-lg border-2 border-gray-200"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
